
import {Component, Vue} from 'vue-property-decorator';
import dayjs from "dayjs";
import {DataloggerFirmware} from "@/data/models/datalogger";
import {extractFirmwareVersion, MIN_SPIFFS_APP_SIZE} from "@/helpers/esp-flash-files";

/**
 * Firmwares des dataloggers ESP32, communs à tous les boîtiers : upload, liste, suppression.
 * Ils sont ensuite installés boîtier par boîtier depuis l'onglet Datalogger de la fiche compteur (OTA).
 */

const ESP_IMAGE_MAGIC = 0xe9;
const VERSION_PATTERN = /^[A-Za-z0-9._-]{1,32}$/;

interface FileCheck {
  ok: boolean;
  warning?: boolean;
  text: string;
}

@Component({})
export default class DataloggerFirmwares extends Vue {
  firmwares: Array<DataloggerFirmware> = [];
  loading: boolean = true;

  file: File = null;
  fileChecks: FileCheck[] = [];
  detectedVersion: string = null;
  version: string = '';
  comment: string = '';
  dragging: boolean = false;
  uploading: boolean = false;
  deleting: { [id: string]: boolean } = {};
  helpPanel: number = null;

  headers = [
    {text: 'Version', value: 'version'},
    {text: 'Fichier', value: 'filename'},
    {text: 'Taille', value: 'size', align: 'right'},
    {text: 'MD5', value: 'md5', sortable: false},
    {text: 'Commentaire', value: 'comment', sortable: false},
    {text: 'Uploadé le', value: 'createdAt'},
    {text: '', value: 'action', align: 'right', sortable: false},
  ];

  get versionRules(): Array<(v: string) => boolean | string> {
    return [
      (v: string): boolean | string => !!v || 'Version obligatoire (FW_VERSION du sketch)',
      (v: string): boolean | string => VERSION_PATTERN.test(v || '') || 'Lettres, chiffres, « . », « _ » ou « - » (32 max)',
      (v: string): boolean | string => !this.firmwares.some((f) => f.version === v) || 'Cette version est déjà uploadée',
    ];
  }

  mounted(): void {
    this.load();
  }

  // ************************************
  // Liste
  // ************************************

  async load(): Promise<void> {
    this.loading = true;
    try {
      this.firmwares = await this.$dao.datalogger.getFirmwares();
    } catch (error) {
      this.showError(error, 'Impossible de charger la liste des firmwares');
    } finally {
      this.loading = false;
    }
  }

  formatDate(date: string | null): string {
    return date ? dayjs(date).format('DD.MM.YYYY HH:mm') : '';
  }

  formatSize(size: number): string {
    return size >= 1024 * 1024 ? `${(size / 1024 / 1024).toFixed(2).replace('.', ',')} Mo` : `${(size / 1024).toFixed(0)} Ko`;
  }

  usage(size: number): number {
    return Math.round((size / MIN_SPIFFS_APP_SIZE) * 100);
  }

  copy(text: string): void {
    navigator.clipboard.writeText(text).then(() => this.$dialog.notify.success('MD5 copié'));
  }

  showError(error: { json?: { error?: string } }, fallback: string): void {
    const message = error?.json?.error || fallback;
    this.$dialog.error({text: message, title: 'Erreur'});
  }

  // ************************************
  // Sélection du fichier et contrôles
  // ************************************

  get canUpload(): boolean {
    return !!this.file && this.fileChecks.every((check) => check.ok);
  }

  pickFile(): void {
    (this.$refs.fileInput as HTMLInputElement).click();
  }

  onFileInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.selectFile(input.files[0]);
    }
    input.value = '';
  }

  onDrop(event: DragEvent): void {
    this.dragging = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.selectFile(file);
    }
  }

  clearFile(): void {
    this.file = null;
    this.fileChecks = [];
    this.detectedVersion = null;
    this.version = '';
    this.comment = '';
    (this.$refs.form as any)?.resetValidation();
  }

  async selectFile(file: File): Promise<void> {
    this.file = file;
    this.fileChecks = [];
    this.detectedVersion = null;

    const name = file.name;
    if (!name.endsWith('.bin')) {
      this.fileChecks.push({ok: false, text: 'Fichier .bin attendu.'});
      return;
    }
    if (name.endsWith('.merged.bin')) {
      this.fileChecks.push({ok: false, text: 'Image fusionnée (.merged.bin) : uploader le fichier « .ino.bin » de l\'application.'});
      return;
    }
    if (name.endsWith('.bootloader.bin') || name.endsWith('.partitions.bin') || name === 'boot_app0.bin') {
      this.fileChecks.push({ok: false, text: 'Ce n\'est pas l\'application : uploader le fichier « .ino.bin ».'});
      return;
    }

    const data = new Uint8Array(await file.arrayBuffer());
    if (data[0] !== ESP_IMAGE_MAGIC) {
      this.fileChecks.push({ok: false, text: 'Ce fichier n\'est pas une image ESP32.'});
      return;
    }
    this.fileChecks.push({ok: true, text: 'Image d\'application ESP32'});

    const usage = this.usage(data.length);
    if (data.length > MIN_SPIFFS_APP_SIZE) {
      this.fileChecks.push({ok: false, text: `Trop grand pour l'emplacement OTA (${usage} %).`});
    } else {
      this.fileChecks.push({ok: true, warning: usage > 90, text: `${usage} % de l'emplacement OTA (Minimal SPIFFS)${usage > 90 ? ' : les prochaines versions risquent de ne plus passer' : ''}`});
    }

    // Version : chaîne « Firmware AAAA-MM-JJ » du binaire, sinon date en tête du nom du sketch
    this.detectedVersion = extractFirmwareVersion(data);
    const fromName = /^(\d{4}-\d{2}-\d{2})/.exec(name);
    if (this.detectedVersion) {
      this.fileChecks.push({ok: true, text: `Version ${this.detectedVersion} lue dans le binaire`});
    } else {
      this.fileChecks.push({ok: true, warning: true, text: 'Version absente du binaire (sketch antérieur à 2026-10-16 ?) : à vérifier.'});
    }
    if (!this.version) {
      this.version = this.detectedVersion || (fromName ? fromName[1] : '');
    }
  }

  // ************************************
  // Upload et suppression
  // ************************************

  async upload(): Promise<void> {
    if (!(this.$refs.form as any).validate() || !this.canUpload) {
      return;
    }

    this.uploading = true;
    try {
      const firmware = await this.$dao.datalogger.uploadFirmware(this.file, this.version, this.comment);
      this.$dialog.notify.success(`Firmware ${firmware.version} uploadé`);
      this.clearFile();
      await this.load();
    } catch (error) {
      this.showError(error, "Impossible d'uploader le firmware");
    } finally {
      this.uploading = false;
    }
  }

  remove(firmware: DataloggerFirmware): void {
    this.$dialog
        .confirm({
          actions: [{text: 'Annuler', key: false}, {text: 'Supprimer', key: true}],
          title: 'Suppression du firmware',
          text: `Supprimer le firmware ${firmware.version} (${firmware.filename || 'sans nom'}) ?`,
        })
        .then(async (confirmed) => {
          if (!confirmed) {
            return;
          }
          this.$set(this.deleting, firmware.id, true);
          try {
            await this.$dao.datalogger.deleteFirmware(firmware.id);
            await this.load();
          } catch (error) {
            this.showError(error, 'Impossible de supprimer le firmware');
          } finally {
            this.$set(this.deleting, firmware.id, false);
          }
        });
  }
}
